import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { normalizarOit } from '../../../compartilhado/oit.ts';
import { FolhaOit } from '../componentes/FolhaOit.tsx';
import { api } from '../lib/api.ts';
import { dataBr, idade } from '../lib/formato.ts';
import './impressao.css';

/* Esta tela existe para virar PDF. É a MESMA folha que o médico marcou, em modo
   somente leitura -- o documento entregue ao paciente não é uma segunda versão
   do formulário, é a primeira, congelada. */

interface Dados {
  exame: Record<string, string | number | null>;
  laudo: {
    tipo: 'TEXTO' | 'OIT_2000';
    conteudoHtml: string | null;
    dadosOit: unknown;
    medicoNome: string;
    medicoCrm: string;
    assinadoEm: string | null;
  } | null;
  imagens: { id: string; titulo: string | null; dataUrl: string }[];
  /** Chapas que o exame tem mas cujo arquivo nao pode ser lido. */
  imagensAusentes: number;
  clinica: { nome: string; endereco: string; telefone: string } | null;
  assinaturaDataUrl: string | null;
  /** Tamanho da assinatura, em % do padrao, vindo do cadastro do medico. */
  assinaturaEscala: number;
  logoDataUrl: string | null;
}

/** O corpo do laudo é HTML de verdade, ou texto que o médico digitou?
 *
 *  Só as tags que o laudo usa contam. Testar por "<" qualquer pegaria medida
 *  ("< 3 mm"), que é justamente o caso que não pode ser lido como marcação. */
function temMarcacao(t: string | null): boolean {
  return /<\/?(p|div|br|ul|ol|li|strong|b|em|i|span|h[1-6])\b[^>]*>/i.test(t ?? '');
}

export function Impressao() {
  const { exameId } = useParams<{ exameId: string }>();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  /* Papel branco, sempre -- mesmo com o sistema no tema escuro.

     São duas coisas, e a segunda não é óbvia. O atributo faz o CSS pintar html
     e body de branco. Mas a moldura escura que sobrava no PDF não vinha do
     `background`: vinha do `color-scheme: dark` que o index.html grava como
     estilo INLINE no <html>, antes do primeiro pixel. Com ele, o Chrome pinta
     a área do canvas em #121212 por conta própria, e nenhuma regra de CSS
     alcança isso -- estilo inline ganha da folha de estilo.

     Por isso o `colorScheme` é reescrito aqui e restaurado ao sair: o resto do
     sistema continua escuro para quem escolheu escuro. */
  useEffect(() => {
    const raiz = document.documentElement;
    const esquemaAnterior = raiz.style.colorScheme;
    raiz.setAttribute('data-folha', '');
    raiz.style.colorScheme = 'light';
    return () => {
      raiz.removeAttribute('data-folha');
      raiz.style.colorScheme = esquemaAnterior;
    };
  }, []);

  const { data, isPending, error } = useQuery({
    queryKey: ['impressao', exameId, token],
    queryFn: ({ signal }) =>
      api.get<Dados>(`/laudos/impressao/${exameId}?t=${encodeURIComponent(token)}`, signal),
    retry: false,
  });

  if (isPending) return <p className="impressao__aviso">Carregando…</p>;
  /* Sem laudo ainda há documento: o exame que o paciente leva para o médico
     dele é só cabeçalho e chapas. O que não existe é folha sem nada -- nem
     laudo nem imagem não é documento, é papel em branco com timbre. */
  if (error || !data || (!data.laudo && data.imagens.length === 0)) {
    return <p className="impressao__aviso">Não foi possível montar o documento.</p>;
  }

  const { exame, laudo, imagens, clinica } = data;
  const rodape = clinica ? `${clinica.nome} - ${clinica.endereco} - Tel:${clinica.telefone}` : '';
  const nascimento = String(exame.pacienteNascimento ?? '');

  return (
    // O gerador de PDF espera por este atributo: sem ele, imprimiria a tela de
    // carregamento.
    <div className="impressao" data-impressao-pronta>
      {laudo?.tipo === 'OIT_2000' ? (
        <FolhaOit
          somenteLeitura
          dados={normalizarOit(laudo.dadosOit)}
          aoMudar={() => {}}
          cabecalho={{
            funcionario: String(exame.pacienteNome ?? ''),
            empresa: String(exame.empresaNome ?? ''),
            dataRx: dataBr(String(exame.dataExame ?? '')),
            dataLeitura: dataBr((laudo.assinadoEm ?? '').slice(0, 10)),
            clinica: clinica?.nome ?? '',
            logo: data.logoDataUrl,
            rodape,
          }}
          assinatura={{
            url: data.assinaturaDataUrl,
            escala: data.assinaturaEscala,
            nome: laudo.medicoNome,
            crm: laudo.medicoCrm,
          }}
        />
      ) : (
        <article className="laudo-texto">
          <header className="laudo-texto__topo">
            {data.logoDataUrl ? (
              <img className="laudo-texto__logo" src={data.logoDataUrl} alt={clinica?.nome ?? ''} />
            ) : (
              <span className="laudo-texto__marca">{clinica?.nome}</span>
            )}
            <span className="laudo-texto__data">Data: {dataBr(String(exame.dataExame ?? ''))}</span>
          </header>

          <dl className="laudo-texto__dados">
            <div>
              <dt>Código:</dt>
              <dd className="mono">{String(exame.codigo)}</dd>
            </div>
            <div>
              <dt>Nome:</dt>
              <dd>{String(exame.pacienteNome ?? '')}</dd>
            </div>
            <div>
              <dt>Idade:</dt>
              <dd>
                {idade(nascimento)} Anos ({dataBr(nascimento)})
              </dd>
            </div>
            {exame.pacienteCpf && (
              <div>
                <dt>CPF:</dt>
                <dd className="mono">{String(exame.pacienteCpf)}</dd>
              </div>
            )}
            <div>
              <dt>Convênio:</dt>
              <dd>{String(exame.convenioNome ?? exame.empresaNome ?? 'PARTICULAR')}</dd>
            </div>
          </dl>

          <h1 className="laudo-texto__procedimento">{String(exame.procedimentoNome ?? '')}</h1>

          {laudo ? (
            <>
              <p className="laudo-texto__rotulo">LAUDO</p>

              {/* O editor de laudo é uma textarea: o médico escreve texto puro,
                  uma linha por achado. Jogar isso em innerHTML colapsaria as
                  quebras e o laudo sairia como um parágrafo corrido -- e um
                  "< 3 mm" viraria abertura de tag. Só tratamos como HTML o que
                  de fato tem marcação, que são os laudos antigos. */}
              {temMarcacao(laudo.conteudoHtml) ? (
                <div
                  className="laudo-texto__corpo"
                  dangerouslySetInnerHTML={{ __html: laudo.conteudoHtml ?? '' }}
                />
              ) : (
                <div className="laudo-texto__corpo laudo-texto__corpo--simples">
                  {laudo.conteudoHtml ?? ''}
                </div>
              )}

              {/* A escala vem do cadastro do médico e vira uma variável do CSS:
                  a altura da assinatura é calculada a partir dela, e o resto do
                  bloco (nome e CRM) não se mexe. */}
              <div
                className="laudo-texto__assinatura"
                style={{ '--assinatura-escala': data.assinaturaEscala / 100 } as React.CSSProperties}
              >
                {data.assinaturaDataUrl && <img src={data.assinaturaDataUrl} alt="" />}
                <p>{laudo.medicoNome}</p>
                <p>CRM:{laudo.medicoCrm}</p>
              </div>
            </>
          ) : (
            /* Exame entregue sem laudo. Dizer isso é a parte mais importante da
               folha: quem recebe precisa saber que nenhum médico leu estas
               imagens aqui, senão o timbre da clínica passa a impressão de
               laudo -- e a ausência de texto viraria "não há alterações".

               A folha é a mesma nos dois modos, e o texto não: no PORTAL o
               laudo existe e sai por outro caminho; no SEM_LAUDO ele não existe
               e ninguém vai escrevê-lo. Repetir "a pedido do paciente" no
               Portal seria afirmar algo falso num documento com timbre. */
            <div className="laudo-texto__corpo laudo-texto__sem-laudo">
              {exame.modoEnvio === 'PORTAL' ? (
                <>
                  <p className="laudo-texto__rotulo">IMAGENS DO EXAME</p>
                  <p>
                    Este documento acompanha{' '}
                    {imagens.length === 1 ? 'a imagem' : 'as imagens'} do exame acima.{' '}
                    <strong>Não é um laudo</strong>: o laudo deste exame é entregue pelo portal,
                    por fora desta clínica.
                  </p>
                  <p>
                    {imagens.length === 1
                      ? '1 imagem segue nas próximas páginas.'
                      : `${imagens.length} imagens seguem nas próximas páginas.`}
                  </p>
                </>
              ) : (
                <>
                  <p className="laudo-texto__rotulo">EXAME SEM LAUDO</p>
                  <p>
                    Este documento acompanha{' '}
                    {imagens.length === 1 ? 'a imagem' : 'as imagens'} do exame acima.{' '}
                    <strong>Não é um laudo</strong>: as imagens não foram interpretadas nesta
                    clínica, a pedido do paciente.
                  </p>
                  <p>
                    {imagens.length === 1
                      ? '1 imagem segue nas próximas páginas.'
                      : `${imagens.length} imagens seguem nas próximas páginas.`}{' '}
                    Leve este documento ao médico que solicitou o exame.
                  </p>
                </>
              )}
            </div>
          )}

          <footer className="laudo-texto__rodape">{rodape}</footer>
        </article>
      )}

      {data.imagensAusentes > 0 && (
        <section className="impressao__chapa impressao__falta">
          <p>
            {data.imagensAusentes === 1
              ? '1 imagem deste exame nao pode ser lida e nao esta neste documento.'
              : `${data.imagensAusentes} imagens deste exame nao puderam ser lidas e nao estao neste documento.`}
            <br />
            {laudo
              ? 'Procure a clinica antes de usar este laudo.'
              : 'Procure a clinica antes de usar este documento.'}
          </p>
        </section>
      )}

      {imagens.map((im) => (
        <section className="impressao__chapa" key={im.id}>
          <img src={im.dataUrl} alt="Radiografia" />
        </section>
      ))}
    </div>
  );
}
